import { useEffect, useId, useRef, useState } from 'react';
import { HEBBIAN_PAIRS, SIGNAL_NOISE_WEIGHTS } from '../../../../../packages/signals/src/types';
import type { SignalState } from '../../types';
import './SignalField.css';

const SIGNALS: { key: keyof SignalState; label: string; color: string }[] = [
  { key: 'seeking', label: 'Seeking', color: '#337bcd' },
  { key: 'care', label: 'Care', color: '#d56b99' },
  { key: 'play', label: 'Play', color: '#d38b39' },
  { key: 'energy', label: 'Energy', color: '#3298ad' },
  { key: 'satisfaction', label: 'Satisfaction', color: '#529b7c' },
  { key: 'fear', label: 'Fear', color: '#8c76c3' },
  { key: 'panic', label: 'Panic', color: '#c86779' },
  { key: 'rage', label: 'Rage', color: '#c55050' },
  { key: 'shame', label: 'Shame', color: '#b17eae' },
  { key: 'boredom', label: 'Boredom', color: '#7b8d9f' },
  { key: 'confusion', label: 'Confusion', color: '#a89041' },
  { key: 'nostalgia', label: 'Nostalgia', color: '#9b86bb' },
  { key: 'existentialAnxiety', label: 'Existential anxiety', color: '#6b799d' },
  { key: 'arousal', label: 'Arousal', color: '#bc718b' },
];

function reading(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}
const percent = (value: number | null) => value === null ? 'No reading' : `${Math.round(value * 100)}%`;

export function SignalField({ signals, baselines, stale = false }: {
  signals: Partial<SignalState>;
  baselines: Partial<SignalState>;
  stale?: boolean;
}) {
  const id = useId().replace(/:/g, '');
  const [selected, setSelected] = useState<keyof SignalState>('seeking');
  const [angle, setAngle] = useState(-0.55);
  const [moving, setMoving] = useState(false);
  const [motionAllowed, setMotionAllowed] = useState(false);
  const drag = useRef<{ x: number; angle: number } | null>(null);

  useEffect(() => {
    const preference = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!preference) return;
    const update = () => { setMotionAllowed(!preference.matches); setMoving(!preference.matches); };
    update();
    preference.addEventListener('change', update);
    return () => preference.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!moving || !motionAllowed || stale) return;
    let frame = 0;
    let previous = 0;
    const animate = (time: number) => {
      if (!document.hidden && time - previous >= 50) {
        // Camera motion only. Neither values nor network strength are synthesized.
        setAngle(a => a + 0.0018);
        previous = time;
      }
      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [moving, motionAllowed, stale]);

  const points = SIGNALS.map(signal => {
    const axis = SIGNAL_NOISE_WEIGHTS[signal.key];
    const x = axis.v * 205;
    const y = -(axis.a - 0.1) * 178;
    const z = (axis.s - 0.15) * 220;
    const depth = x * Math.sin(angle) + z * Math.cos(angle);
    const scale = 570 / (570 - depth);
    const value = reading(signals[signal.key]);
    return { ...signal, value, x: 180 + (x * Math.cos(angle) - z * Math.sin(angle)) * scale,
      y: 176 + y * scale + depth * 0.12, depth, scale,
      radius: (value === null ? 9 : 10 + Math.max(0, Math.min(1, value)) * 24) * scale };
  });
  const chosen = points.find(p => p.key === selected)!;
  const baseline = reading(baselines[selected]);
  const choose = (key: keyof SignalState) => { setSelected(key); setMoving(false); };

  return (
    <section className="signal-field" aria-labelledby={`${id}-title`} data-stale={stale || undefined}>
      <div className="field-heading">
        <div><p className="section-index">01 / INNER STATE</p><h3 id={`${id}-title`}>Signal field</h3></div>
        <span className="field-count">14 signals</span>
      </div>
      <div className="field-stage">
        <svg viewBox="0 0 360 338" className="field-svg" aria-label="Three-dimensional signal field"
          onPointerDown={e => {
            if ((e.target as Element).closest('[data-signal]')) return;
            drag.current = { x: e.clientX, angle }; setMoving(false);
            e.currentTarget.setPointerCapture(e.pointerId);
          }}
          onPointerMove={e => { if (drag.current) setAngle(drag.current.angle + (e.clientX - drag.current.x) / 160); }}
          onPointerUp={() => { drag.current = null; }}
          onPointerCancel={() => { drag.current = null; }}>
          <defs>
            {SIGNALS.map(s => <radialGradient key={s.key} id={`${id}-${s.key}`} cx="31%" cy="24%" r="76%">
              <stop offset="0" stopColor="#ffffff" /><stop offset="0.15" stopColor="#ffffff" stopOpacity="0.88" />
              <stop offset="0.46" stopColor={s.color} stopOpacity="0.44" />
              <stop offset="0.82" stopColor={s.color} stopOpacity="0.92" />
              <stop offset="1" stopColor={s.color} />
            </radialGradient>)}
            <radialGradient id={`${id}-shadow`}><stop stopColor="#8997ac" stopOpacity="0.2" /><stop offset="1" stopColor="#8997ac" stopOpacity="0" /></radialGradient>
          </defs>
          <ellipse cx="180" cy="298" rx="139" ry="26" fill={`url(#${id}-shadow)`} />
          <g className="field-orbits" fill="none" stroke="#c5cdd8" strokeWidth="0.7" aria-hidden="true">
            <ellipse cx="180" cy="170" rx="148" ry="66" transform="rotate(-24 180 170)" />
            <ellipse cx="180" cy="170" rx="110" ry="148" transform="rotate(27 180 170)" strokeDasharray="2 6" />
          </g>
          <g aria-hidden="true">
            {HEBBIAN_PAIRS.map(([a, b]) => {
              const from = points.find(p => p.key === a)!;
              const to = points.find(p => p.key === b)!;
              const active = a === selected || b === selected;
              return <line key={`${a}:${b}`} x1={from.x} y1={from.y} x2={to.x} y2={to.y}
                stroke={active ? chosen.color : '#9ba8ba'} strokeWidth={active ? 1.1 : 0.65} opacity={active ? 0.65 : 0.25} />;
            })}
          </g>
          {[...points].sort((a, b) => a.depth - b.depth).map(p => (
            <g key={p.key} data-signal={p.key} role="button" tabIndex={0}
              aria-label={`Inspect ${p.label}, ${percent(p.value).toLowerCase()}`}
              aria-pressed={selected === p.key} className="field-node" transform={`translate(${p.x} ${p.y})`}
              onClick={() => choose(p.key)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choose(p.key); } }}>
              <circle className="node-hit" r={Math.max(18, p.radius + 6)} fill="transparent" />
              <circle className="node-focus" r={p.radius + 5} fill="none" stroke={p.color} strokeWidth="1.5" />
              <circle r={p.radius} fill={p.value === null ? '#ffffff' : `url(#${id}-${p.key})`}
                stroke={p.color} strokeOpacity="0.65" strokeWidth="0.7" />
              <ellipse cx={-p.radius * 0.22} cy={-p.radius * 0.35} rx={p.radius * 0.32} ry={p.radius * 0.15}
                fill="white" opacity="0.55" transform="rotate(-30)" pointerEvents="none" />
              {selected === p.key &&
                <text className="node-label" y={p.radius + 19} textAnchor="middle" pointerEvents="none">{p.label}</text>}
            </g>
          ))}
        </svg>
        <div className="field-controls">
          <button type="button" onClick={() => { setMoving(false); setAngle(a => a - 0.35); }} aria-label="Rotate signal field left">←</button>
          <span>Drag to explore</span>
          <button type="button" onClick={() => { setMoving(false); setAngle(a => a + 0.35); }} aria-label="Rotate signal field right">→</button>
          {motionAllowed && <button type="button" className="field-motion" aria-pressed={moving && !stale} disabled={stale}
            onClick={() => setMoving(v => !v)}>{moving && !stale ? 'Pause motion' : 'Animate'}</button>}
        </div>
      </div>
      <div className="signal-selection" role="region" aria-label="Selected signal" aria-live="polite">
        <div><span className="selected-swatch" style={{ background: chosen.color }} />
          <h4>{chosen.label}</h4><span className="selection-baseline">Baseline {percent(baseline).toLowerCase()}</span></div>
        <strong>{percent(chosen.value)}</strong>
      </div>
      {stale && <p className="field-stale">Last known readings. The latest update failed.</p>}
      <details className="signal-readings">
        <summary>All readings <span>0–100%</span></summary>
        <div className="signal-reading-grid">
          {points.map(p => <button key={p.key} type="button" onClick={() => choose(p.key)} aria-pressed={selected === p.key}>
            <span><i style={{ background: p.color }} />{p.label}</span><b>{percent(p.value)}</b>
          </button>)}
        </div>
      </details>
      <details className="field-basis"><summary>How to read this</summary>
        <p>Sphere size shows signal level on a 0–1 scale, displayed as a percentage. Position uses the model’s valence, activation and social axes. Lines show configured tracked pairs, not measured connection strength. Rotation changes the view, not the readings.</p>
      </details>
    </section>
  );
}
